import React, { useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { FaChevronDown } from 'react-icons/fa';
import {
  Box,
  ControlLabel,
  TextInput,
  Text,
} from '@staffino/primitive-controls';
import * as fnsLocales from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import Tooltip from '../tooltip';

const DatePickerDateInput = ({ onClick, invalid, ...rest }: any) => (
  <div
    className={`p-2 react-datepicker-input ${invalid ? 'invalid' : ''}`}
    onClick={onClick}
    onKeyDown={onClick}
    role="button"
    tabIndex={0}
  >
    <TextInput borderStyle="none" height="2.8rem" {...rest} />
    <div className="ml-auto date-input-icon">
      <FaChevronDown color="gray" />
    </div>
  </div>
);

type Props = {
  type?: string;
  value?: Date;
  defaultValue?: Date;
  invalid?: boolean;
  placeholder?: string;
  label?: string;
  labelShowMandatory?: boolean;
  tooltip?: {
    content: any;
    placement?: any;
    show?: boolean;
  };
  c?: string;
  name?: string;
  limit?: number;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  requiredValidationMessage?: string;
  validationMessage?: string;
  onChange?: any;
} & React.ComponentProps<typeof Box>;

const DateInputControl: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<unknown>
> = React.forwardRef(
  (
    {
      label,
      labelShowMandatory,
      tooltip,
      placeholder,
      onChange,
      value,
      defaultValue,
      invalid,
      requiredValidationMessage,
      ...rest
    }: Props,
    ref,
  ) => {
    const { i18n } = useTranslation();
    const dateFnsLocales = fnsLocales;
    const dateFnsLocale = useMemo(() => {
      return dateFnsLocales[i18n.language] || dateFnsLocales.enUS;
    }, [dateFnsLocales, i18n.language]);

    return (
      <Box {...rest} /* ref={ref} */>
        <div style={{ display: 'flex' }}>
          <Tooltip
            tooltip={tooltip?.content}
            placement={tooltip?.placement}
            show={tooltip?.show}
          >
            <ControlLabel label={label} showMandatory={labelShowMandatory} />
          </Tooltip>
          {!!requiredValidationMessage && (
            <Text color="notification" alignSelf="center">
              {requiredValidationMessage}
            </Text>
          )}
        </div>
        <DatePicker
          portalId="root-portal"
          showPopperArrow={false}
          selected={new Date(value).getTime() || defaultValue}
          placeholderText={placeholder}
          locale={dateFnsLocale}
          dateFormat={dateFnsLocale?.formatLong.date({
            width: 'short',
          })}
          onChange={(date) => {
            onChange?.(format(date, 'yyyy-MM-dd'));
          }}
          customInput={<DatePickerDateInput invalid={invalid} />}
        />
      </Box>
    );
  },
);

export default DateInputControl;
